import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { operationApi } from '../../services/api';

export default function OperationPositionsPage() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await operationApi.profiles.list({ per_page: 100 });
      // Extract unique positions from profiles
      const allProfiles = res.data.data?.data || res.data.data || [];
      const uniquePositions = [];
      const seen = new Set();
      allProfiles.forEach(p => {
        if (p.position && !seen.has(p.position)) {
          seen.add(p.position);
          uniquePositions.push({ id: p.position, name: p.position, count: allProfiles.filter(x => x.position === p.position).length });
        }
      });
      setPositions(uniquePositions);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', is_active: true }); setShowForm(true); };
  const openEdit = (pos) => { setEditing(pos); setForm({ name: pos.name, description: pos.description || '', is_active: pos.is_active !== false }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert('Nama posisi wajib diisi');
    setSaving(true);
    try {
      // Positions are stored as a string field on profiles, so we manage them via a simple API
      // For now, we use the profiles endpoint to manage position metadata
      if (editing) {
        await operationApi.profiles.update(editing.id, { position: form.name });
      } else {
        // Create a placeholder profile entry for the position (or use a dedicated endpoint)
        await operationApi.profiles.create({ name: form.name, position: form.name, is_active: form.is_active });
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (pos) => {
    if (!window.confirm(`Hapus posisi "${pos.name}"?`)) return;
    try {
      // Update all profiles with this position to empty
      const res = await operationApi.profiles.list({ per_page: 500 });
      const allProfiles = res.data.data?.data || res.data.data || [];
      const affected = allProfiles.filter(p => p.position === pos.name);
      await Promise.all(affected.map(p => operationApi.profiles.update(p.id, { position: '' })));
      fetchData();
    } catch (err) { alert('Gagal menghapus'); }
  };

  return (
    <AdminLayout active="Posisi">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-lg font-semibold text-gray-800 mb-1">Posisi</h1>
            <p className="text-sm text-gray-500 mt-1">Data master posisi/jabatan pekerja operasi</p>
          </div>
          <button onClick={openCreate} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark">+ Tambah Posisi</button>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Posisi</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jumlah Pekerja</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {positions.map((pos) => (
                  <tr key={pos.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{pos.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{pos.count || 0} pekerja</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Aktif</span></td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => openEdit(pos)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                      <button onClick={() => handleDelete(pos)} className="text-red-600 hover:text-red-800 text-sm">Hapus</button>
                    </td>
                  </tr>
                ))}
                {positions.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Belum ada posisi</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none z-10">&times;</button>
              <h2 className="text-lg font-bold mb-4 pr-8">{editing ? 'Edit Posisi' : 'Tambah Posisi'}</h2>
              <form onSubmit={handleSave} className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Nama Posisi *</label><input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="contoh: Teknisi, Supervisor, Operator" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={2} placeholder="Deskripsi singkat posisi" /></div>
                <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Batal</button>
                  <button type="submit" disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark disabled:opacity-50">{saving ? 'Menyimpan...' : 'Simpan'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}